import { IFormOperation, IFormOperationAnswerField, IFormOperationAnswerStep, ITextLocaleMap } from '../models/operations.models';
import { transformDate } from '../../core/utils/utils';
import {
	isFormOperationCalendarField,
	isFormOperationMatchOcrField,
	shouldIncludeFormOperationDetailField,
} from './form-operation-detail-display.util';
import {
	buildFormMatchDataErrorKeySet,
	IFormMatchDataError,
	isFormFieldMatchDataMismatch,
} from './form-operation-match-data.util';

/**
 * Agrupa respuestas FORM del detalle operación/proceso en secciones semánticas (Figma `9260-133626`).
 * No agrupa por paso del journey: aplana todos los `fields` de `answers[]` y los clasifica por `field.id`.
 */
/** Secciones visibles del acordeón «Datos» (Figma `9260-133626`). */
export type FormOperationDetailSectionId = 'personal_data' | 'identity_document' | 'address' | 'other_data';

export interface IFormOperationDetailFieldView extends IFormOperationAnswerField {
	showCalendar?: boolean;
	ageYears?: number;
	/** Muestra icon-eye para comparar con OCR (Figma `9260-133626`). */
	matchOcr?: boolean;
	/** Cotejo OCR en KO (`match_ocr_result.status`). */
	matchOcrMismatch?: boolean;
	/** Título del paso FORM (pauta) para etiquetas custom sin `field.title`. */
	stepTitle?: string | ITextLocaleMap;
}

export interface IFormOperationDetailSectionView {
	id: FormOperationDetailSectionId;
	titleKey: string;
	fields: IFormOperationDetailFieldView[];
}

const SECTION_TITLE_KEYS: Record<FormOperationDetailSectionId, string> = {
	personal_data: 'OPERATIONS.DETAIL.FORM.SECTION_PERSONAL_DATA',
	identity_document: 'OPERATIONS.DETAIL.FORM.SECTION_IDENTITY_DOCUMENT',
	address: 'OPERATIONS.DETAIL.FORM.SECTION_ADDRESS',
	other_data: 'OPERATIONS.DETAIL.FORM.SECTION_OTHER_DATA',
};

const SECTION_ORDER: FormOperationDetailSectionId[] = [
	'personal_data',
	'identity_document',
	'address',
	'other_data',
];

/** `field.id` / `field.name` → sección Figma (presets workflow FORM). */
const FIELD_SECTION_BY_ID: Record<string, FormOperationDetailSectionId> = {
	surname: 'personal_data',
	surname2: 'personal_data',
	second_surname: 'personal_data',
	first_surname: 'personal_data',
	surnames: 'personal_data',
	name: 'personal_data',
	names: 'personal_data',
	gender: 'personal_data',
	nationality: 'personal_data',
	birth_date: 'personal_data',
	birthdate: 'personal_data',

	id_document: 'identity_document',
	id_document_type: 'identity_document',
	document_type: 'identity_document',
	document_number: 'identity_document',
	identifier: 'identity_document',
	personal_id: 'identity_document',
	expiration_date: 'identity_document',
	expiry_date: 'identity_document',
	expiry: 'identity_document',

	address: 'address',
	address_number: 'address',
	address_floor: 'address',
	address_door: 'address',
	zip_code: 'address',
	city: 'address',
	municipality: 'address',
	province: 'address',
	country: 'address',

	email: 'other_data',
	phone: 'other_data',
	phone_prefix: 'other_data',
};

const FIELD_ORDER_BY_SECTION: Record<FormOperationDetailSectionId, string[]> = {
	personal_data: [
		'surname',
		'first_surname',
		'surnames',
		'surname2',
		'second_surname',
		'name',
		'names',
		'gender',
		'nationality',
		'birth_date',
		'birthdate',
	],
	identity_document: [
		'id_document_type',
		'document_type',
		'id_document',
		'document_number',
		'identifier',
		'personal_id',
		'expiration_date',
		'expiry_date',
		'expiry',
	],
	address: [
		'address',
		'address_number',
		'address_floor',
		'address_door',
		'zip_code',
		'city',
		'municipality',
		'country',
		'province',
	],
	other_data: ['email', 'phone_prefix', 'phone'],
};

const AGE_FIELD_IDS = new Set(['birth_date', 'birthdate']);

type IFormFieldConfigMeta = Partial<Pick<IFormOperationAnswerField, 'options' | 'validators' | 'title' | 'type'>>;

function hasResolvableTitle(field: Pick<IFormOperationAnswerField, 'title'>): boolean {
	if (field.title == null) {
		return false;
	}
	if (typeof field.title === 'string') {
		return field.title.trim().length > 0;
	}
	return Object.values(field.title).some((value) => String(value ?? '').trim().length > 0);
}

function resolveFieldKey(field: IFormOperationAnswerField): string {
	return (field.id || field.name || '').trim().toLowerCase();
}

function resolveSectionId(field: IFormOperationAnswerField): FormOperationDetailSectionId {
	const key = resolveFieldKey(field);
	return FIELD_SECTION_BY_ID[key] ?? 'other_data';
}

function sortIndex(sectionId: FormOperationDetailSectionId, field: IFormOperationAnswerField): number {
	const key = resolveFieldKey(field);
	const order = FIELD_ORDER_BY_SECTION[sectionId];
	const index = order.indexOf(key);
	return index === -1 ? order.length + 1 : index;
}

function enrichFieldView(
	field: IFormOperationAnswerField,
	matchDataErrorKeys: Set<string>,
	stepTitle?: string | ITextLocaleMap,
): IFormOperationDetailFieldView {
	const key = resolveFieldKey(field);
	const view: IFormOperationDetailFieldView = { ...field, stepTitle };

	if (isFormOperationCalendarField(field)) {
		view.showCalendar = true;
	}
	if (AGE_FIELD_IDS.has(key) && field.value) {
		view.ageYears = computeAgeYears(field.value);
	}
	view.matchOcr = isFormOperationMatchOcrField(field);
	view.matchOcrMismatch =
		field.match_ocr_result?.status === 'KO' || isFormFieldMatchDataMismatch(field, matchDataErrorKeys);

	return view;
}

function computeAgeYears(value: string): number | undefined {
	const parsed = transformDate(value) ?? parseLooseDate(value);
	if (!parsed) {
		return undefined;
	}
	const today = new Date();
	let age = today.getFullYear() - parsed.getFullYear();
	const monthDelta = today.getMonth() - parsed.getMonth();
	if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < parsed.getDate())) {
		age--;
	}
	return age >= 0 ? age : undefined;
}

/** Acepta `dd/MM/yyyy`, ISO u otros formatos que entienda `transformDate`. */
function parseLooseDate(value: string): Date | undefined {
	const trimmed = value.trim();
	const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
	if (slashMatch) {
		const day = Number(slashMatch[1]);
		const month = Number(slashMatch[2]) - 1;
		const year = Number(slashMatch[3]);
		const date = new Date(year, month, day);
		return Number.isNaN(date.getTime()) ? undefined : date;
	}
	return undefined;
}

/**
 * Índice de metadatos de config por campo (`options`, `validators`).
 * Clave por `uuid` y por `id`/`name` como fallback.
 */
function buildFieldConfigIndex(
	config: IFormOperation['config'] | null | undefined,
): Map<string, IFormFieldConfigMeta> {
	const index = new Map<string, IFormFieldConfigMeta>();
	for (const step of config?.steps ?? []) {
		for (const field of step.fields ?? []) {
			const meta: IFormFieldConfigMeta = {};
			if (field.options?.length) {
				meta.options = field.options;
			}
			if (field.validators?.length) {
				meta.validators = field.validators;
			}
			if (hasResolvableTitle(field)) {
				meta.title = field.title;
			}
			if (field.type) {
				meta.type = field.type;
			}
			if (!Object.keys(meta).length) {
				continue;
			}
			if (field.uuid) {
				index.set(`uuid:${field.uuid}`, meta);
			}
			const key = (field.id || field.name || '').trim().toLowerCase();
			if (key) {
				index.set(`key:${key}`, meta);
			}
		}
	}
	return index;
}

/** Completa metadatos del campo de respuesta desde config si no vienen ya. */
function withConfigFieldMeta(
	field: IFormOperationAnswerField,
	configIndex: Map<string, IFormFieldConfigMeta>,
): IFormOperationAnswerField {
	const byUuid = field.uuid ? configIndex.get(`uuid:${field.uuid}`) : undefined;
	const byKey = configIndex.get(`key:${(field.id || field.name || '').trim().toLowerCase()}`);
	const meta = byUuid ?? byKey;
	if (!meta) {
		return field;
	}
	return {
		...field,
		...(meta.options?.length && !field.options?.length ? { options: meta.options } : {}),
		...(meta.validators?.length && !field.validators?.length ? { validators: meta.validators } : {}),
		...(meta.title && !hasResolvableTitle(field) ? { title: meta.title } : {}),
		...(meta.type && !field.type ? { type: meta.type } : {}),
	};
}

function resolveConfigFieldValidators(
	field: IFormOperationAnswerField,
	configIndex: Map<string, IFormFieldConfigMeta>,
): string[] | null {
	const byUuid = field.uuid ? configIndex.get(`uuid:${field.uuid}`) : undefined;
	const byKey = configIndex.get(`key:${(field.id || field.name || '').trim().toLowerCase()}`);
	return byUuid?.validators ?? byKey?.validators ?? null;
}

/**
 * Agrupa `answers[]` en secciones Figma omitiendo bloques vacíos.
 * `config` (opcional) aporta las `options[]` i18n para resolver valores de select/radio/checkbox.
 */
export function groupFormOperationAnswersBySection(
	answers: IFormOperationAnswerStep[] | null | undefined,
	config?: IFormOperation['config'] | null,
	matchDataErrors?: IFormMatchDataError[] | null,
): IFormOperationDetailSectionView[] {
	if (!answers?.length) {
		return [];
	}

	const configIndex = buildFieldConfigIndex(config);
	const matchDataErrorKeys = buildFormMatchDataErrorKeySet(matchDataErrors ?? []);
	const buckets = new Map<FormOperationDetailSectionId, IFormOperationDetailFieldView[]>();

	for (const step of answers) {
		for (const rawField of step.fields ?? []) {
			const configValidators = resolveConfigFieldValidators(rawField, configIndex);
			if (!shouldIncludeFormOperationDetailField(rawField, configValidators)) {
				continue;
			}
			const field = withConfigFieldMeta(rawField, configIndex);
			const sectionId = resolveSectionId(field);
			const bucket = buckets.get(sectionId) ?? [];
			bucket.push(enrichFieldView(field, matchDataErrorKeys, step.title));
			buckets.set(sectionId, bucket);
		}
	}

	return SECTION_ORDER.filter((sectionId) => buckets.has(sectionId)).map((sectionId) => {
		const fields = [...(buckets.get(sectionId) ?? [])].sort(
			(a, b) => sortIndex(sectionId, a) - sortIndex(sectionId, b),
		);
		return {
			id: sectionId,
			titleKey: SECTION_TITLE_KEYS[sectionId],
			fields,
		};
	});
}
